// PRD 11 §3.6 — 상담 내용 block editor. Owns the add / delete / reorder chrome
// for the block array; each block type renders only its body. Saving with 0
// blocks is allowed (PRD §3.4). At most 10 blocks (PRD §3.4) — the add buttons
// disable once the cap is reached. Reorder uses the native HTML5 drag API (no
// dependency); only the ≡ handle is draggable so it never fights the rich-text
// caret or the inputs (mirrors the Health Encyclopedia editor).

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Trash2 } from 'lucide-react';

import type { ContentBlock } from '../types/counseling-manual.types';
import { MAX_BLOCKS } from '../utils/validate-form';
import { ImageBlockEditor } from './ImageBlockEditor';
import { TextBlockEditor } from './TextBlockEditor';
import { VideoBlockEditor } from './VideoBlockEditor';

interface ContentBlockListProps {
  blocks: ContentBlock[];
  error?: string;
  // CONTENT_LIB:READ detail view (PRD §7) — the whole editor is locked: no
  // add / reorder / delete, and every block body renders read-only.
  disabled?: boolean;
  onAddText: () => void;
  onAddImage: () => void;
  onAddVideo: () => void;
  onChangeBlock: (id: number, patch: Partial<ContentBlock>) => void;
  onDeleteBlock: (id: number) => void;
  onReorder: (from: number, to: number) => void;
}

export function ContentBlockList({
  blocks,
  error,
  disabled = false,
  onAddText,
  onAddImage,
  onAddVideo,
  onChangeBlock,
  onDeleteBlock,
  onReorder,
}: ContentBlockListProps) {
  const { t } = useTranslation('counseling-manual');
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const atBlockLimit = blocks.length >= MAX_BLOCKS;

  const handleDrop = (target: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOver(null);
    if (from == null || from === target) return;
    onReorder(from, target);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
        <button type="button" className="btn btn-default btn-secondary" disabled={disabled || atBlockLimit} onClick={onAddText}>
          T {t('blocks.addText')}
        </button>
        <button type="button" className="btn btn-default btn-secondary" disabled={disabled || atBlockLimit} onClick={onAddImage}>
          🖼 {t('blocks.addImage')}
        </button>
        <button type="button" className="btn btn-default btn-secondary" disabled={disabled || atBlockLimit} onClick={onAddVideo}>
          🎬 {t('blocks.addVideo')}
        </button>
        {atBlockLimit ? (
          <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
            {t('blocks.limitReached', { max: MAX_BLOCKS })}
          </span>
        ) : null}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxHeight: 360,
          overflow: 'auto',
          border: `1px solid ${error ? 'var(--destructive)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-md)',
          padding: 8,
          background: '#fff',
        }}
      >
        {blocks.length === 0 ? (
          <div
            style={{
              padding: 16,
              border: '1px dashed var(--border)',
              borderRadius: 'var(--radius-sm)',
              textAlign: 'center',
              color: 'var(--muted-foreground)',
              fontSize: 13,
            }}
          >
            {t('blocks.empty')}
          </div>
        ) : (
          blocks.map((block, index) => (
            <div
              key={block.id}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(index);
              }}
              onDrop={() => handleDrop(index)}
              style={{
                border: `1px solid ${dragOver === index ? 'var(--info)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
                padding: 8,
                background: '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-default btn-secondary"
                    title={t('blocks.dragHandle')}
                    aria-label={t('blocks.dragHandle')}
                    draggable={!disabled}
                    disabled={disabled}
                    onDragStart={() => {
                      dragIndex.current = index;
                    }}
                    onDragEnd={() => {
                      dragIndex.current = null;
                      setDragOver(null);
                    }}
                    style={{ cursor: 'grab', height: 28, padding: '0 6px' }}
                  >
                    <GripVertical size={14} />
                  </button>
                  <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
                    {t('blocks.position', { index: index + 1, type: t(`blocks.types.${block.type}`) })}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => onDeleteBlock(block.id)}
                  className="btn btn-default btn-secondary"
                  disabled={disabled}
                  style={{ height: 28, padding: '0 8px', color: 'var(--destructive)' }}
                  title={t('blocks.delete')}
                  aria-label={t('blocks.delete')}
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {block.type === 'text' ? (
                <TextBlockEditor block={block} disabled={disabled} onChange={(patch) => onChangeBlock(block.id, patch)} />
              ) : block.type === 'image' ? (
                <ImageBlockEditor block={block} disabled={disabled} onChange={(patch) => onChangeBlock(block.id, patch)} />
              ) : (
                <VideoBlockEditor block={block} disabled={disabled} onChange={(patch) => onChangeBlock(block.id, patch)} />
              )}
            </div>
          ))
        )}
      </div>
      {error ? (
        <p className="form-field__error" style={{ marginTop: 4 }}>
          {error}
        </p>
      ) : null}
    </>
  );
}
